import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ExclamationCircleIcon,
  CurrencyPoundIcon,
  CurrencyDollarIcon,
  CurrencyEuroIcon,
  ReceiptPercentIcon,
} from "@heroicons/react/24/solid";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import Card from "../layout/Card";
import SwitchToggle from "../forms/SwitchToggle";
import DangerZone from "../forms/DangerZone";
import Indicator from "../layout/Indicator";
import UploadFileDropzone from "../forms/UploadImageDropzone";
import {
  PaymentItemState,
  createPaymentItem,
  deletePaymentItems,
  getPaymentItems,
  updatePaymentItem,
} from "../../slices/payment";
import Dropdown from "../forms/Dropdown";
import NumberInput from "../forms/NumberInput";
import { getSolidIcon } from "../../utils/icons";
import Filters from "../layout/Filters";
import { useSearchParams } from "react-router-dom";
import DisabledTextInput from "../forms/DisabledTextInput";

export default function Items() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const searchCategory = searchParams.get("category");

  // Fetch data from server
  useEffect(() => {
    dispatch(getPaymentItems({ category: searchCategory }));
  }, [dispatch, searchCategory]);

  const [selectedItem, setSelectedItem] = useState<{
    id: string;
    request_option: string;
    description: string | null;
    price: string;
    tax_percentage: string;
    image: any;
    status: string;
  }>({
    id: "",
    request_option: "",
    description: "",
    price: "0.00",
    tax_percentage: "0.00",
    image: null,
    status: "ACT",
  });
  const [createItemForm, setCreateItemForm] = useState<{
    request_option: string;
    description: string | null;
    price: string;
    tax_percentage: string;
    status: string;
  }>({
    request_option: "",
    description: "",
    price: "0.00",
    tax_percentage: "0.00",
    status: "ACT",
  });

  const [category, setCategory] = useState({ value: "" });
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { items, options } = useAppSelector((state) => state.payment);
  const { images } = useAppSelector((state) => state.uploads);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectItemToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [itemId] = args;
    const itemData = itemsData.filter((item) => item.id === itemId)[0];
    setSelectedItem({
      id: itemData.id,
      request_option: itemData.request_option[0],
      description: itemData.description,
      price: itemData.price,
      tax_percentage: itemData.tax_percentage,
      image: itemData.image,
      status: itemData.status[0],
    });
    setShowEditModal(true);
  }

  function openCreateItemModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  async function confirmCreateItem(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      request_option: requestOption,
      description,
      price,
      tax_percentage: taxPercentage,
      status,
    } = createItemForm;

    // Validate text fields
    if (requestOption === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_request_option", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    if (price === "") {
      dispatch(
        addAlert(generateAlertText("missing_price", language), "danger", 5000),
      );
      return;
    }
    if (taxPercentage === "") {
      dispatch(
        addAlert(generateAlertText("missing_price", language), "danger", 5000),
      );
      return;
    }

    // Add item image if uploaded
    const processedPhotoKeys = Object.keys(images.payment_items.data);
    const processedPhotos = [];
    for (let i = 0; i < processedPhotoKeys.length; i += 1) {
      const key = processedPhotoKeys[i];
      const candidate = images.payment_items.data[key];
      if (candidate.data === null || !candidate.success) {
        continue;
      }
      processedPhotos.push({
        id: key,
        source: "processed",
        data: candidate.data,
      });
    }

    setShowCreateModal(false);
    const res: any = await dispatch(
      createPaymentItem({
        property: user.property.id,
        requestOption,
        description,
        price,
        taxPercentage,
        image: processedPhotos.length > 0 ? processedPhotos[0] : null,
        status,
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(
          generateAlertText("create_payment_item", language),
          "success",
          3000,
        ),
      );
      setCreateItemForm({
        request_option: "",
        description: "",
        price: "0.00",
        tax_percentage: "0.00",
        status: "ACT",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditItem(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      id,
      request_option: requestOption,
      description,
      price,
      tax_percentage: taxPercentage,
      status,
    } = selectedItem;

    // Validate text fields
    if (requestOption === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_request_option", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    if (price === "") {
      dispatch(
        addAlert(generateAlertText("missing_price", language), "danger", 5000),
      );
      return;
    }
    if (taxPercentage === "") {
      dispatch(
        addAlert(generateAlertText("missing_price", language), "danger", 5000),
      );
      return;
    }

    // Confirm and validate image
    const savedImage: any = selectedItem.image;

    // Build object for processed photos
    const processedPhotoKeys = Object.keys(images.payment_items.data);
    const processedPhotos: any = [];
    for (let i = 0; i < processedPhotoKeys.length; i += 1) {
      const key = processedPhotoKeys[i];
      const candidate = images.payment_items.data[key];
      if (candidate.data === null || !candidate.success) {
        continue;
      }
      processedPhotos.push({
        id: key,
        source: "processed",
        data: candidate.data,
      });
    }

    let editImage: any = null;
    if (
      !images.payment_items.removed.includes(savedImage) &&
      savedImage !== null
    ) {
      editImage = { id: 0, source: "saved", data: savedImage };
    } else {
      editImage = processedPhotos.length > 0 ? processedPhotos[0] : null;
    }

    setShowEditModal(false);
    const res: any = await dispatch(
      updatePaymentItem({
        paymentItemId: id,
        property: user.property.id,
        requestOption,
        description,
        price,
        taxPercentage,
        image: editImage,
        status,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_payment_item", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeleteItems(e: any, args: any[]) {
    e.preventDefault();
    const [itemIds] = args;
    if (itemIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (itemIds === null || itemIds === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_payment_items", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(
      deletePaymentItems({ paymentItemIds: [itemIds] }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function getClassName(field: string, value: string) {
    if (field === "price") {
      const floatValue = parseFloat(value);
      const check = floatValue >= 0 && floatValue <= 1000000;
      if (check)
        return "block w-full appearance-none rounded-md border border-success bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-success focus:bg-light/40 focus:outline-none focus:ring-success sm:text-sm";
      return "block w-full appearance-none rounded-md border border-danger bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-danger focus:bg-light/40 focus:outline-none focus:ring-danger sm:text-sm";
    }
    if (field === "tax") {
      const floatValue = parseFloat(value);
      const check = floatValue >= 0 && floatValue <= 100;
      if (check)
        return "block w-full appearance-none rounded-md border border-success bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-success focus:bg-light/40 focus:outline-none focus:ring-success sm:text-sm";
      return "block w-full appearance-none rounded-md border border-danger bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-danger focus:bg-light/40 focus:outline-none focus:ring-danger sm:text-sm";
    }
    return "block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm";
  }

  function getSavedImages() {
    if (selectedItem === null) return [];
    if (selectedItem.image === null) return [];
    if (images.payment_items.removed.includes(selectedItem.image)) return [];
    return [{ id: selectedItem.image, data: selectedItem.image }];
  }

  // Format data
  const itemsData = items.data === null ? [] : items.data;
  const requestOptions =
    options.remaining_request_options === undefined
      ? []
      : options.remaining_request_options;
  const requestCategories =
    options.category === undefined
      ? []
      : Object.keys(options.category).map((key) => ({
          value: key,
          label: options.category[key][language],
        }));

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("payment_items_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateItemModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_payment_item", language)}
              </Button>
            }
          />
        }
      />

      {/* Create payment item modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_payment_item", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateItem}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_payment_item", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Request option */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("request_option", language)}
                showLabel={true}
                required={true}
                state={createItemForm}
                stateName="request_option"
                onChange={setCreateItemForm}
                defaultValue=""
              >
                <option value=""></option>
                {requestOptions.map((opt: [string, string]) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Description */}
              <TextInput
                id="description"
                type="text"
                label={translateText("description", language)}
                placeholder={translateText(
                  "description_payment_item_placeholder",
                  language,
                )}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createItemForm}
                stateName="description"
                onChange={setCreateItemForm}
              />

              {/* Price */}
              <NumberInput
                id="price"
                label={`${translateText("price", language)} (${user?.property
                  .currency})`}
                className={getClassName("price", createItemForm.price)}
                required={true}
                showLabel={true}
                state={createItemForm}
                stateName="price"
                onChange={setCreateItemForm}
              />

              {/* Tax */}
              <NumberInput
                id="tax"
                label={`${translateText("tax", language)} %`}
                className={getClassName("tax", createItemForm.tax_percentage)}
                required={true}
                showLabel={true}
                state={createItemForm}
                stateName="tax_percentage"
                onChange={setCreateItemForm}
                max={100.0}
              />

              {/* Image */}
              <div>
                <label
                  htmlFor="payment_item_image"
                  className={`mb-3 block text-left text-sm font-medium text-black`}
                >
                  {translateText("item_image", language)}
                </label>
                <UploadFileDropzone
                  id="payment_item_image"
                  className=""
                  limit={1}
                  page="payment_items"
                  savedImages={[]}
                  blockId={null}
                />
              </div>

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                description={translateText(
                  "status_payment_item_subtext",
                  language,
                )}
                showLabel={true}
                toggle={createItemForm.status === "ACT"}
                state={createItemForm}
                stateName="status"
                handler={setCreateItemForm}
                mapping={{ true: "ACT", false: "INAC" }}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit payment item modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_payment_item", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditItem}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_payment_item", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Request option */}
              {itemsData.filter((x) => x.id === selectedItem.id).length > 0 && (
                <DisabledTextInput
                  id="request_option"
                  label={translateText("request_option", language)}
                  className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                  value={
                    itemsData.filter((x) => x.id === selectedItem.id)[0]
                      .request_option[1]
                  }
                  showLabel={true}
                />
              )}

              {/* Description */}
              <TextInput
                id="description"
                type="text"
                label={translateText("description", language)}
                placeholder={translateText(
                  "description_payment_item_placeholder",
                  language,
                )}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedItem}
                stateName="description"
                onChange={setSelectedItem}
              />

              {/* Price */}
              <NumberInput
                id="price"
                label={`${translateText("price", language)} (${user?.property
                  .currency})`}
                className={getClassName("price", selectedItem.price)}
                required={true}
                showLabel={true}
                state={selectedItem}
                stateName="price"
                onChange={setSelectedItem}
              />

              {/* Tax */}
              <NumberInput
                id="tax"
                label={`${translateText("tax", language)} %`}
                className={getClassName("tax", selectedItem.tax_percentage)}
                required={true}
                showLabel={true}
                state={selectedItem}
                stateName="tax_percentage"
                onChange={setSelectedItem}
                max={100.0}
              />

              {/* Item image */}
              <div>
                <label
                  className={`mb-3 block text-left text-sm font-medium text-black`}
                >
                  {translateText("item_image", language)}
                </label>
                <UploadFileDropzone
                  id="payment_item_edit_image"
                  className=""
                  limit={1}
                  page="payment_items"
                  savedImages={getSavedImages()}
                  blockId={null}
                />
              </div>

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                description={translateText(
                  "status_payment_item_subtext",
                  language,
                )}
                showLabel={true}
                toggle={selectedItem.status === "ACT"}
                state={selectedItem}
                stateName="status"
                handler={setSelectedItem}
                mapping={{ true: "ACT", false: "INAC" }}
              />

              {/* Danger zone */}
              <DangerZone
                objectId={selectedItem.id}
                handler={confirmDeleteItems}
                language={language}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {items.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "category",
                  name: "Category",
                  options: requestCategories,
                },
              ]}
              queryParams={[["category", searchCategory]]}
              handlers={{
                category: {
                  state: category,
                  stateName: "value",
                  onChange: setCategory,
                },
              }}
              loading={items.loading}
              baseUrl="/payment/items"
              showDateRange={false}
              showFilters={true}
              showSearchBar={false}
            />
            {itemsData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {itemsData.map((item: PaymentItemState) => (
                    <a
                      href="#!"
                      key={item.id}
                      onClick={(e) => selectItemToEdit(e, [item.id])}
                      className="relative"
                    >
                      {/* Payment item data */}
                      <Card
                        key={item.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="flex flex-nowrap items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {item.request_option[1]}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          {/* Category */}
                          <div className="inline-flex text-sm text-secondary">
                            {getSolidIcon(item.category[0], "info", 5, "mr-1")}
                            {item.category[1]}
                          </div>

                          {/* Price */}
                          {user?.property.currency === "GBP" && (
                            <div className="inline-flex text-sm text-secondary">
                              <CurrencyPoundIcon
                                className="h-5 w-5 text-secondary mr-1"
                                aria-hidden="true"
                              />
                              {item.price}
                            </div>
                          )}
                          {user?.property.currency === "USD" && (
                            <div className="inline-flex text-sm text-secondary">
                              <CurrencyDollarIcon
                                className="h-5 w-5 text-info mr-1"
                                aria-hidden="true"
                              />
                              {item.price}
                            </div>
                          )}
                          {user?.property.currency === "EUR" && (
                            <div className="inline-flex text-sm text-secondary">
                              <CurrencyEuroIcon
                                className="h-5 w-5 text-info mr-1"
                                aria-hidden="true"
                              />
                              {item.price}
                            </div>
                          )}

                          {/* Tax */}
                          <div className="inline-flex text-sm text-secondary">
                            <ReceiptPercentIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {item.tax_percentage}%
                          </div>
                        </div>
                      </Card>

                      {/* Status indicator */}
                      <Indicator
                        size={3}
                        colour={
                          item.status[0] === "ACT" ? "success" : "warning"
                        }
                        pulse={false}
                      />
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_payment_items", language)}
                subtext={translateText("no_payment_items_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
